/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/pages/Capture.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useRef, useState } from 'react';
import { Camera, FileUp, Check, CircleAlert, Layers, Send, Trash2, LoaderCircle, LockKeyhole, RotateCcw } from 'lucide-react';
import '../styles/capture.css';

/**
 * Belege erfassen (ADR-050): the page behind a personal upload link, for phones, without sign-in. The link's secret
 * is in the URL fragment (/erfassen#kul_…), which the browser never sends to the server; the page passes it to the API
 * in a header. Photos are taken with the phone's camera (input capture), each file is sent on its own with a progress
 * bar, and several photos can be sent as one multi-page Beleg. The page shows only what this phone sent.
 */

const HEADER = 'x-kontor-upload-token';
interface Info { tenant: string; label: string; expiresAt: string; maxFiles: number; maxBytes: number; maxPages: number }
interface Received { filename: string; size: number; sha256: string; pages: number; seal?: { id: string; convertedToPdf: boolean; sealedPdfSha256: string } }
type State = 'uploading' | 'done' | 'error';
interface Item { key: number; name: string; size: number; files: File[]; combine: boolean; state: State; progress: number; error?: string; got?: Received }

const tokenOf = () => { const h = location.hash.replace(/^#/, ''); return /^kul_[A-Za-z0-9_-]{43}$/.test(h) ? h : null; };
const mb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const day = (iso: string) => new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** One request with upload progress (fetch has none). Resolves with the server's answer, rejects with its German error. */
function send(token: string, files: File[], combine: boolean, onProgress: (p: number) => void): Promise<Received[]> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    files.forEach((f, i) => fd.append(`file${i}`, f, f.name));
    if (combine) fd.append('combine', '1');
    fd.append('seal', '1');
    const x = new XMLHttpRequest();
    x.open('POST', '/api/inbound/link/files');
    x.setRequestHeader(HEADER, token);
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    x.onload = () => {
      let data: { received?: Received[]; error?: string } = {};
      try { data = JSON.parse(x.responseText); } catch { /* not JSON */ }
      if (x.status === 200 && data.received) resolve(data.received);
      else reject(new Error(data.error ?? `Senden fehlgeschlagen (${x.status})`));
    };
    x.onerror = () => reject(new Error('Keine Verbindung. Bitte erneut senden.'));
    x.send(fd);
  });
}

export function CapturePage() {
  const [token] = useState(tokenOf);
  const [info, setInfo] = useState<Info | null>(null);
  const [fatal, setFatal] = useState<string | null>(token ? null : 'Dieser Link ist unvollständig. Bitte den ganzen Link oder den QR-Code verwenden.');
  const [items, setItems] = useState<Item[]>([]);
  const [multi, setMulti] = useState(false);
  const [pages, setPages] = useState<File[]>([]);
  const seq = useRef(0);
  const camera = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = 'Belege erfassen · Kontor';
    const other = () => location.reload(); // another link opened in the same tab
    window.addEventListener('hashchange', other);
    return () => window.removeEventListener('hashchange', other);
  }, []);
  useEffect(() => {
    if (!token) return;
    fetch('/api/inbound/link/info', { headers: { [HEADER]: token } })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error ?? 'Link nicht gültig'); setInfo(d); })
      .catch((e: Error) => setFatal(e.message));
  }, [token]);

  const patch = (key: number, p: Partial<Item>) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x)));

  /** Sends one Beleg (one file, or the pages of one Beleg); the list shows its progress and the answer. */
  const upload = async (files: File[], combine: boolean, existing?: Item) => {
    if (!token || !info) return;
    const key = existing?.key ?? ++seq.current;
    const size = files.reduce((n, f) => n + f.size, 0);
    const name = combine ? `Beleg mit ${files.length} Seiten` : files[0].name || 'Foto';
    const item: Item = { key, name, size, files, combine, state: 'uploading', progress: 0 };
    setItems((xs) => (existing ? xs.map((x) => (x.key === key ? item : x)) : [item, ...xs]));
    const big = files.find((f) => f.size > info.maxBytes);
    if (big) { patch(key, { state: 'error', error: `Grösser als ${mb(info.maxBytes)}: bitte mit tieferer Auflösung fotografieren` }); return; }
    try {
      const [got] = await send(token, files, combine, (p) => patch(key, { progress: p }));
      patch(key, { state: 'done', progress: 1, got });
    } catch (e) { patch(key, { state: 'error', error: (e as Error).message }); }
  };

  const picked = async (list: FileList | null) => {
    const files = [...(list ?? [])];
    if (!files.length) return;
    if (multi) { setPages((p) => [...p, ...files].slice(0, info?.maxPages ?? 20)); return; }
    for (const f of files) await upload([f], false); // one after the other: each with its own progress
  };

  const sendPages = async () => { const p = pages; setPages([]); await upload(p, true); };
  const done = items.filter((x) => x.state === 'done').length;

  if (fatal) {
    return (
      <main className="capture">
        <Brand />
        <section className="capture-card capture-fatal" role="alert"><CircleAlert aria-hidden /><div><h1>Link nicht verwendbar</h1><p>{fatal}</p></div></section>
      </main>
    );
  }
  return (
    <main className="capture">
      <Brand />
      <header className="capture-head">
        <h1>Belege erfassen</h1>
        {info ? <p><span>für <b>{info.tenant}</b></span><span className="capture-sub">{info.label} · gültig bis {day(info.expiresAt)}</span></p> : <p className="capture-sub"><LoaderCircle className="spin" aria-hidden /> Link wird geprüft …</p>}
      </header>

      <section className="capture-card">
        <input ref={camera} type="file" accept="image/*,application/pdf" capture="environment" multiple hidden onChange={(e) => { void picked(e.target.files); e.target.value = ''; }} />
        <input ref={picker} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple hidden onChange={(e) => { void picked(e.target.files); e.target.value = ''; }} />
        <div className="capture-actions">
          <button className="capture-btn primary" disabled={!info} onClick={() => camera.current?.click()}><Camera aria-hidden /> {multi && pages.length ? 'Nächste Seite fotografieren' : 'Foto aufnehmen'}</button>
          <button className="capture-btn" disabled={!info} onClick={() => picker.current?.click()}><FileUp aria-hidden /> Datei wählen</button>
        </div>
        <label className="capture-toggle">
          <input type="checkbox" checked={multi} disabled={pages.length > 0} onChange={(e) => setMulti(e.target.checked)} />
          <span><Layers aria-hidden /> Mehrseitiger Beleg: Fotos zu einem Beleg zusammenfassen</span>
        </label>
        {multi && (
          <div className="capture-pages" aria-live="polite">
            <p>{pages.length ? `${pages.length} ${pages.length === 1 ? 'Seite' : 'Seiten'} bereit` : 'Seiten nacheinander fotografieren, dann senden.'}</p>
            {pages.length > 0 && (
              <div className="capture-actions">
                <button className="capture-btn primary" onClick={sendPages}><Send aria-hidden /> Beleg senden ({pages.length} {pages.length === 1 ? 'Seite' : 'Seiten'})</button>
                <button className="capture-btn" onClick={() => setPages([])}><Trash2 aria-hidden /> Verwerfen</button>
              </div>
            )}
          </div>
        )}
      </section>

      {items.length > 0 && (
        <section className="capture-card" aria-live="polite">
          <h2>Gesendet{done ? `: ${done} ${done === 1 ? 'Beleg' : 'Belege'} empfangen` : ''}</h2>
          <ul className="capture-list">
            {items.map((x) => (
              <li key={x.key} data-state={x.state}>
                <span className="capture-state" aria-hidden>{x.state === 'done' ? <Check /> : x.state === 'error' ? <CircleAlert /> : <LoaderCircle className="spin" />}</span>
                <span className="capture-item">
                  <b>{x.got?.filename ?? x.name}</b>
                  <span className="capture-sub">
                    {x.state === 'uploading' && `${Math.round(x.progress * 100)} % von ${mb(x.size)}`}
                    {x.state === 'done' && x.got && `Empfangen · ${mb(x.got.size)}${x.got.pages > 1 ? ` · ${x.got.pages} Seiten` : ''}${x.got.seal ? ` · versiegelt (${x.got.seal.convertedToPdf ? 'PDF konvertiert' : 'PDF'})` : ''} · SHA-256 ${x.got.sha256.slice(0, 12)}…`}
                    {x.state === 'error' && x.error}
                  </span>
                  {x.state === 'uploading' && <span className="capture-bar"><span style={{ width: `${Math.round(x.progress * 100)}%` }} /></span>}
                </span>
                {x.state === 'error' && <button className="capture-btn sm" onClick={() => void upload(x.files, x.combine, x)} aria-label="Erneut senden"><RotateCcw aria-hidden /></button>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="capture-note"><LockKeyhole aria-hidden /> Dieser Link kann nur Belege hochladen. Er zeigt keine Daten des Mandanten an. Die Belege werden unverändert archiviert und von Kontor verbucht; Rückfragen kommen von Ihrem Treuhänder.</p>
    </main>
  );
}

function Brand() {
  return (
    <div className="capture-brand">
      <span className="brand-mark" aria-hidden><svg viewBox="0 0 32 32" width="16" height="16"><path d="M10 7v18M10 16l9-8M13.5 13l8 11" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" /></svg></span>
      <span>Kontor</span>
    </div>
  );
}
